import cuid from "cuid";

import type {
  CreateWorkspaceScheduledBroadcastResponse,
  NotificationFeedPreference,
  WorkspaceInvitation,
  WorkspaceRole,
  WsFeedPreference,
  WsWorkflowItem,
} from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("feed");

import * as api from "@storyboard/web-client";
import { transport } from "@/config";
import { appContext, deviceContext } from "@/models/apiContext";
import { bulkUpsertBroadcastAction } from "@/models/repositories/BroadcastAction";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertScheduleTrigger } from "@/models/repositories/ScheduleTrigger";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { bulkUpsertWorkflowItem, deleteWsDraft } from "@/models/repositories/WorkflowItem";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import { store } from "@/store/store";
import { bulkUpsertBroadcastRecipients } from "../repositories";

export async function deleteItem(itemId: string) {
  const feedItem = store.get("item", itemId);
  const feedRecord = store.get("feed", feedItem?.feedId);
  if (!feedRecord || !feedRecord.workspaceId || !itemId) return;

  const res = await api.deleteWorkspaceFeedItem(transport, {
    workspaceId: feedRecord.workspaceId,
    feedId: feedRecord.id,
    itemId,
  });

  if (res?.item) {
    await bulkUpsertItemAndCalculateMembership([res.item]);
  }
}

export async function sendWorkspaceInvites(
  workspaceId: string,
  emails: string[] = [],
  phoneNumbers: string[] = [],
  sendEmail = false,
  invites: WorkspaceInvitation[] = [],
) {
  const res = await api.createWorkspaceInvitations(transport, {
    workspaceId,
    emails,
    phoneNumbers,
    sendEmail,
    invitations: invites,
  });
  const invitations = res?.invitations || [];
  const workspaceMemberships = res?.workspaceMemberships || [];
  if (invitations.length) await bulkUpsertDirectWsInvitation(invitations);
  if (workspaceMemberships.length) await bulkUpsertWorkspaceMembership(workspaceMemberships);
  return {
    invitations,
    workspaceMemberships,
  };
}

export async function updateMemberRole(workspaceId: string, workspaceMembershipIds: string[], role: WorkspaceRole) {
  const resp = await api.updateWorkspaceMember(transport, { workspaceId, workspaceMembershipIds, role });
  if (resp?.workspaceMemberships?.length) await bulkUpsertWorkspaceMembership(resp.workspaceMemberships);
}

export async function removeMember(workspaceId: string, workspaceMembershipIds: string[]) {
  const resp = await api.removeWorkspaceMember(transport, { workspaceId, workspaceMembershipIds });
  if (resp?.workspaceMemberships?.length) await bulkUpsertWorkspaceMembership(resp.workspaceMemberships);
}

export async function subscribeToFeed({
  workspaceId,
  feedId,
  workspaceMembershipId,
  preference,
}: {
  workspaceId: string;
  feedId: string;
  workspaceMembershipId: string;
  preference: NotificationFeedPreference;
}): Promise<WsFeedPreference> {
  if (!preference) {
    throw new Error("Preference is required for subscribing to a feed");
  }
  const response = await api.subscribeToFeed(transport, {
    workspaceId,
    feedId,
    workspaceMembershipId,
    preference,
  });
  if (response?.feedPreference) {
    await bulkUpsertFeedPreference([response.feedPreference]);
  }
  return response?.feedPreference ?? null;
}

export async function unsubscribeFromFeed({
  workspaceId,
  feedId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  feedId: string;
  workspaceMembershipId: string;
}): Promise<WsFeedPreference> {
  const { feedPreference } = await api.unsubscribeFromFeed(transport, {
    workspaceId,
    feedId,
    workspaceMembershipId,
  });
  await bulkUpsertFeedPreference([feedPreference]);
  return feedPreference;
}

export async function subscribeToFeedGroup({
  workspaceId,
  feedGroupId,
  workspaceMembershipId,
  preference,
}: {
  workspaceId: string;
  feedGroupId: string;
  workspaceMembershipId: string;
  preference: NotificationFeedPreference;
}) {
  const { feedPreferences } = await api.subscribeToFeedGroup(transport, {
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
    preference,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function unsubscribeFromFeedGroup({
  workspaceId,
  feedGroupId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  feedGroupId: string;
  workspaceMembershipId: string;
}) {
  const { feedPreferences } = await api.unsubscribeFromFeedGroup(transport, {
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
  });
  await bulkUpsertFeedPreference(feedPreferences);
  return feedPreferences;
}

export async function createWorkflowItem(workspaceId: string, contentId?: string, displayName?: string, text?: string) {
  // The old client generated missing ids; the endpoint takes them explicitly.
  const payload = contentId
    ? { contentId, workspaceId, displayName, workflowItemId: cuid() }
    : { displayName, inputText: text, workspaceId, contentId: cuid(), workflowItemId: cuid() };

  const workflowItem: WsWorkflowItem = await api.createWorkspaceWorkflowItem(transport, payload);

  if (!workflowItem) throw new Error("WorkflowItem not created");
  await bulkUpsertWorkflowItem([workflowItem]);
  return workflowItem;
}

export async function deleteWorkflowItem(workspaceId: string, workflowItemId: string) {
  await api.deleteWorkspaceWorkflowItem(transport, { workspaceId, workflowItemId });
  await deleteWsDraft(workflowItemId);
}

export async function createScheduledWorkflowItem({
  workspaceId,
  workflowItemId,
  feedIds,
  scheduledDate,
  scheduledCron,
  timezone,
}: {
  workspaceId: string;
  workflowItemId: string;
  feedIds: Array<string>;
  scheduledDate?: string;
  scheduledCron?: string;
  timezone?: string;
}): Promise<CreateWorkspaceScheduledBroadcastResponse> {
  let props: Parameters<typeof api.createScheduledBroadcast>[1] = {
    workspaceId: workspaceId,
    feedIds,
    workflowItemId: workflowItemId,
  };

  if (scheduledDate) {
    props = { ...props, date: scheduledDate };
  }

  if (scheduledCron) {
    props = { ...props, cron: scheduledCron, timezone: timezone };
  }

  const scheduledBroadcast = await api.createScheduledBroadcast(transport, props);

  if (!scheduledBroadcast) throw new Error("scheduledBroadcast not created");

  log.debug("created broadcast", scheduledBroadcast);
  if (scheduledBroadcast) {
    if (scheduledBroadcast?.scheduleTrigger) {
      await bulkUpsertScheduleTrigger([scheduledBroadcast?.scheduleTrigger]);
    }

    if (scheduledBroadcast?.broadcastAction) {
      await bulkUpsertBroadcastAction([scheduledBroadcast?.broadcastAction]);
    }

    if (scheduledBroadcast?.broadcastRecipients) {
      await bulkUpsertBroadcastRecipients(scheduledBroadcast?.broadcastRecipients || []);
    }
  }

  return scheduledBroadcast;
}

export async function updateScheduledWorkflowItem({
  workspaceId,
  scheduleId,
  feedIds,
  scheduledDate,
  scheduledCron,
  timezone,
}: {
  workspaceId: string;
  scheduleId: string;
  feedIds: Array<string>;
  scheduledDate?: string;
  scheduledCron?: string;
  timezone?: string;
}) {
  const props: {
    workspaceId: string;
    feedIds: string[];
    scheduleId: string;
    date?: string;
    cron?: string;
    timezone?: string;
  } = { workspaceId, feedIds, scheduleId };
  if (scheduledDate) props.date = scheduledDate;
  if (scheduledCron) {
    props.cron = scheduledCron;
    props.timezone = timezone;
  }

  const scheduledBroadcast = await api.updateScheduledBroadcast(transport, props);
  if (!scheduledBroadcast) throw new Error("scheduledBroadcast not updated");

  if (scheduledBroadcast?.scheduleTrigger) {
    await bulkUpsertScheduleTrigger([scheduledBroadcast?.scheduleTrigger]);
  }

  if (scheduledBroadcast?.broadcastAction) {
    await bulkUpsertBroadcastAction([scheduledBroadcast?.broadcastAction]);
  }

  if (scheduledBroadcast?.broadcastRecipients) {
    await bulkUpsertBroadcastRecipients(scheduledBroadcast.broadcastRecipients);
  }
}

export async function publishToWorkspaceFeed({
  workspaceId,
  feedId,
  itemId,
  contentId,
  groupId,
  text,
  preferredLanguage,
  isSilent,
}: {
  workspaceId: string;
  feedId: string;
  itemId?: string;
  contentId: string;
  groupId?: string;
  text?: string;
  preferredLanguage?: string;
  isSilent?: boolean;
}) {
  const feedItemId = itemId || cuid();
  const res = await api.publishTTS(transport, {
    workspaceId,
    feedId,
    itemId: feedItemId,
    contentId,
    groupId,
    text,
    preferredLanguage,
    isSilent,
    appContext,
    deviceContext,
  });
  if (!res) {
    throw new Error("Failed to publish TTS: received an empty response");
  }
  const { item, links, transcriptions } = res;
  await bulkUpsertItemAndCalculateMembership([item]);
  if (links?.length) await bulkUpsertLink(links);
  if (transcriptions?.length) await bulkUpsertWsTranscriptions(transcriptions);
  return item;
}
